import type { PageSource } from './page-source'
import type { Dimensions } from './types'

export interface ReadingAnchor { page: number; offset: number }
export interface ReadingProgress extends ReadingAnchor { updatedAt: number }
export interface Bookmark extends ReadingProgress { id: string; label: string; createdAt: number }
export interface ReaderState { progress: ReadingProgress | null; bookmarks: Bookmark[]; coverPage: number }

export function assertReadingProgress(progress: ReadingProgress, pageCount: number): void {
  if (!Number.isInteger(progress.page) || progress.page < 0 || progress.page >= pageCount ||
      !Number.isFinite(progress.offset) || progress.offset < 0 || progress.offset > 1 ||
      !Number.isSafeInteger(progress.updatedAt) || progress.updatedAt < 0) {
    throw new RangeError('閱讀位置或更新時間無效')
  }
}

/**
 * 伺服器以較新的權威狀態拒絕 mutation（輸掉 last-write-wins）時的通知。
 * 用來讓 UI 顯示伺服器目前的狀態，而不是暗示過期的寫入已經套用。
 */
export type StateRejection =
  | { kind: 'progress'; progress: ReadingProgress }
  /** bookmark 為 null 代表伺服器上已是 tombstone，這次的新增或更名沒有復活它。 */
  | { kind: 'bookmark'; bookmark: Bookmark | null }

/**
 * 封面縮圖連同尺寸。本地以寬度判斷升級時是否過小；雲端上傳時 API 要求寬高。
 * 由產生器一併回傳，呼叫端不必再解碼一次。
 */
export interface CoverThumbnail { blob: Blob; width: number; height: number }

export interface ReaderStateStore {
  load(): Promise<ReaderState>
  saveProgress(progress: ReadingProgress): Promise<void>
  putBookmark(bookmark: Bookmark): Promise<void>
  deleteBookmark(id: string): Promise<void>
  /** 回傳套用後的權威封面頁；雲端來源以伺服器回應為準，不假設等於傳入的 page。 */
  setCover(page: number, thumbnail: CoverThumbnail): Promise<number>
  /** 還原為預設封面（第一頁），並丟棄自訂縮圖。回傳套用後的權威封面頁。 */
  clearCover(): Promise<number>
  /** 只有以 D1 為真相的雲端來源會通知；本地來源沒有第二個真相來源。 */
  onRejected?(listener: (rejection: StateRejection) => void): void
}

export interface ReaderSession {
  readonly id: string
  readonly title: string
  readonly source: PageSource
  readonly dimensions: readonly (Dimensions | null)[]
  readonly state: ReaderStateStore
  dispose(): Promise<void>
}
